import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useLabels from 'infrastructure/js/hooks/useLabels';
import { api as aiAssistantApi } from '../../../actions/AiAssistant/aiAssistantActions';
import { loadDraft, saveDraft, loadFontSize, saveFontSize } from '../../../utils/AiAssistant/aiAssistantStorage';
import AiAssistantMessageList from './aiAssistantMessageList';
import AiAssistantHistoryPanel from '../AiAssistantHistory/aiAssistantHistoryPanel';
import ClearHistoryIcon from '../../../../assets/svg/ai-clear-history.svg';
import HistoryMenuIcon from '../../../../assets/svg/ai-history-menu.svg';
import SendIcon from 'infrastructure/assets/svg/arrow-up-ai.svg';
import RobotIcon from '../../../../assets/svg/ai-agent-robot.svg';
import './aiAssistantChat.scss';
import {dialogsNames} from '../../../enums/dialogsNames';

const FONT_SIZES = [11, 12, 13, 14, 15, 16, 17, 18, 20, 22, 24];
const FONT_SIZE_DEFAULT = 16;

const getLastPrompt = (messages = [], id) => {
  let lastPrompt = '';
  for (let i = id - 1; i >= 0; i--) {
    const msg = messages.get(i);
    if (msg && msg.get('role') === 'user') {
      lastPrompt = msg.get('text') || '';
      break;
    }
  }
  return lastPrompt;
}

export default function AiAssistantChat({ sData, isVisible = false, onClose }) {
  const [inputText, setInputText] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [fontSize, setFontSize] = useState(() => {
    const stored = loadFontSize();
    return stored && FONT_SIZES.includes(stored) ? stored : FONT_SIZE_DEFAULT;
  });
  const inputRef = useRef(null);
  const sendBtnRef = useRef(null);
  const userClickedDuringLoadingRef = useRef(false);

  const labels = useLabels('mat.ai.assistant.chat.')

  const messages = sData.get('messages');
  const loading = sData.get('loading');
  const streamingStatus = sData.get('streamingStatus');

  // Track whether user clicked somewhere while loading, to avoid stealing focus.
  useEffect(() => {
    if (!loading) {
      return;
    }
    userClickedDuringLoadingRef.current = false;
    const handleMouseDown = () => { userClickedDuringLoadingRef.current = true; };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [loading]);

  // When loading finishes, refocus the input if the user hasn't clicked elsewhere.
  useEffect(() => {
    if (loading) {
      return;
    }
    const timer = setTimeout(() => {
      if (inputRef.current && !userClickedDuringLoadingRef.current) {
        inputRef.current.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [loading]);

  // When the chat becomes visible, load the draft and focus the input.
  useEffect(() => {
    if (!isVisible) {
      return;
    }
    setInputText(loadDraft());
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [isVisible]);

  // Closing the panel should always drop back to the live chat view for next time it opens.
  useEffect(() => {
    if (!isVisible) {
      setShowHistory(false);
    }
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }
    const handleDocKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleDocKeyDown);
    return () => document.removeEventListener('keydown', handleDocKeyDown);
  }, [isVisible, onClose]);

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) {
      return;
    }
    el.style.height = 'auto';
    const style = getComputedStyle(el);
    const lineHeight = parseFloat(style.lineHeight);
    const paddingTop = parseFloat(style.paddingTop);
    const paddingBottom = parseFloat(style.paddingBottom);
    const maxHeight = lineHeight * 5 + paddingTop + paddingBottom;
    const newHeight = Math.min(el.scrollHeight, maxHeight);
    el.style.height = newHeight + 'px';
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';

    if (sendBtnRef.current) {
      const singleRowHeight = lineHeight + paddingTop + paddingBottom;
      sendBtnRef.current.style.height = singleRowHeight + 'px';
    }
  }, [inputText, fontSize]);

  const setInput = (text) => {
    setInputText(text);
    saveDraft(text);
  };

  const handleSend = () => {
    const text = inputText.trim();
    if (!text || loading) {
      return;
    }
    setInput('');
    aiAssistantApi.sendMessage(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFeedback = (id, type) => {
    aiAssistantApi.showFeedbackDialog({
      activeDialog: dialogsNames.AI_ASSISTANT_FEEDBACK_DIALOG,
      dialogData: {
        mode: type,
        lastPrompt: getLastPrompt(messages, id),
        onSuccess: () => {
          aiAssistantApi.setMessageFeedback(id, type);
        },
      },
    });
  };

  const handleFontDecrease = () => {
    const idx = FONT_SIZES.indexOf(fontSize);
    if (idx <= 0) {
      return;
    }
    const newSize = FONT_SIZES[idx - 1];
    setFontSize(newSize);
    saveFontSize(newSize);
  };

  const handleFontIncrease = () => {
    const idx = FONT_SIZES.indexOf(fontSize);
    if (idx >= FONT_SIZES.length - 1) {
      return;
    }
    const newSize = FONT_SIZES[idx + 1];
    setFontSize(newSize);
    saveFontSize(newSize);
  };

  const handleStartNewChatFromHistory = () => {
    aiAssistantApi.startNewChat();
    setShowHistory(false);
  };

  const renderLoadingIndicator = () => {
    if (!loading) {
      return null;
    }

    return (
      <div className="ais-chat-bubble--typing">
        <span className="ais-chat-typing-dot" />
        <span className="ais-chat-typing-dot" />
        <span className="ais-chat-typing-dot" />
        <span className="ais-chat-status-text">{streamingStatus || labels.get('status.thinking')}</span>
      </div>
    );
  };

  return (
    <div className="ais-chat"
      style={/** @type {React.CSSProperties} */ ({ fontSize: `${fontSize}px`, '--ais-chat-font-size': `${fontSize}px` })}
    >
      <div className="ais-chat-header">
        <RobotIcon className="ais-chat-header-icon" />
        <div className="ais-chat-title">
          <span className="ais-chat-title-main">{labels.get('header.title')}</span>
          <span className="ais-chat-title-sub">{labels.get('header.subtitle')}</span>
        </div>
        <div className="ais-chat-toolbar">
          <button
            className="ais-chat-toolbar-btn"
            onClick={() => setShowHistory((v) => !v)}
            title={labels.get(showHistory ? 'header.button.backToChat' : 'header.button.history', showHistory ? 'Back to chat' : 'Chat history')}
            aria-label={labels.get(showHistory ? 'header.button.backToChat' : 'header.button.history', showHistory ? 'Back to chat' : 'Chat history')}
          >
            {showHistory ? <i className="fa fa-angle-left" /> : <HistoryMenuIcon />}
          </button>
          {!showHistory && (
            <>
              <div className="ais-chat-toolbar-spacer" />
              <button
                className="ais-chat-toolbar-btn ais-chat-toolbar-btn--font"
                onClick={handleFontDecrease}
                disabled={FONT_SIZES.indexOf(fontSize) === 0}
                title={labels.get('header.button.decreaseFontSize')}
                aria-label={labels.get('header.button.decreaseFontSize')}
              >
                A-
              </button>
              <button
                className="ais-chat-toolbar-btn ais-chat-toolbar-btn--font"
                onClick={handleFontIncrease}
                disabled={FONT_SIZES.indexOf(fontSize) === FONT_SIZES.length - 1}
                title={labels.get('header.button.increaseFontSize')}
                aria-label={labels.get('header.button.increaseFontSize')}
              >
                A+
              </button>
              <div className="ais-chat-toolbar-spacer" />
              <button
                className="ais-chat-toolbar-btn"
                onClick={() => aiAssistantApi.startNewChat()}
                title={labels.get('header.button.clearChatHistory')}
                aria-label={labels.get('header.button.clearChatHistory')}
              >
                <ClearHistoryIcon />
              </button>
            </>
          )}
        </div>
        <button className="ais-chat-close" onClick={onClose} aria-label={labels.get('header.button.close')}>
          ×
        </button>
      </div>

      {showHistory ? (
        <AiAssistantHistoryPanel labels={labels} onStartNewChat={handleStartNewChatFromHistory} />
      ) : (
        <>
          <AiAssistantMessageList
            messages={messages}
            labels={labels}
            isVisible={isVisible}
            onFeedback={handleFeedback}
            emptyState={(
              <div className="ais-chat-welcome">
                <div className="ais-chat-welcome-icon">
                  <RobotIcon />
                </div>
                <p className="ais-chat-welcome-text">
                  {labels.get('welcome')}
                </p>
              </div>
            )}
          />

          <div className="ais-chat-status-area">
            {renderLoadingIndicator()}
          </div>

          <div className="ais-chat-input-area">
            <textarea
              ref={inputRef}
              className="ais-chat-input"
              value={inputText}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={labels.get('input.placeholder')}
              rows={1}
              disabled={loading}
            />
            <button
              ref={sendBtnRef}
              className="ais-chat-send-btn"
              onClick={handleSend}
              title={labels.get('button.send')}
              disabled={!inputText.trim() || loading}
            >
              <SendIcon />
            </button>
          </div>

          <div className="ais-chat-disclaimer">{labels.get('disclaimer')}</div>
        </>
      )}
    </div>
  );
}

AiAssistantChat.propTypes = {
  sData: PropTypes.object.isRequired,
  isVisible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};
